import { clsx } from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string | undefined;
  defaultValue?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  options: readonly SelectOption[];
  placeholder?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  invalid?: boolean | undefined;
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
  'aria-describedby'?: string | undefined;
  className?: string | undefined;
}

/**
 * A styled select on Radix: keyboard type-ahead, focus management, a native `<select>` mirror for
 * form posts. Looks like a `.field`; the list is a raised hairline panel. Give it a label (`<Label
 * htmlFor={id}>`) or `aria-label`.
 */
export function Select({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder,
  id,
  name,
  disabled,
  required,
  invalid,
  className,
  ...aria
}: SelectProps) {
  return (
    <SelectPrimitive.Root
      {...(value !== undefined ? { value } : {})}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      {...(onValueChange ? { onValueChange } : {})}
      {...(name ? { name } : {})}
      {...(disabled ? { disabled } : {})}
      {...(required ? { required } : {})}
    >
      <SelectPrimitive.Trigger
        id={id}
        className={clsx('field select-trigger', className)}
        aria-invalid={invalid || undefined}
        {...aria}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon className="select-trigger__icon">
          <ChevronDown size={18} aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="select-content"
          position="popper"
          sideOffset={6}
          data-lenis-prevent=""
        >
          <SelectPrimitive.Viewport className="select-content__viewport">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="select-item"
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="select-item__check">
                  <Check size={16} aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
